import {
  type AgendaItem,
  type AgendaItemStatus,
  useListAgendaItems,
} from "@elation/api-client/src/stitch-web";
import { Text } from "@src/components/general/Text";
import {
  HorizontalFlexbox,
  VerticalFlexbox,
} from "@src/components/layout/Flexbox";
import { Spinner } from "@src/components/ui/spinner";
import { FileText } from "lucide-react";

export interface CompletedAgendaItemsProps {
  encounterId: number;
}

// Record<AgendaItemStatus, boolean> (not a string array) so adding a new
// status to the union becomes a TS error here, forcing a deliberate
// include/exclude decision rather than silent omission.
const isCompletedStatus = {
  finalized: true,
  was_discussed: true,
  suggested: false,
  open: false,
  actively_discussing: false,
  rejected: false,
  cancelled: false,
  snoozed: false,
} as const satisfies Record<AgendaItemStatus, boolean>;

const isCompleted = (item: AgendaItem): boolean =>
  isCompletedStatus[item.status];

export const CompletedAgendaItems = ({
  encounterId,
}: CompletedAgendaItemsProps) => {
  const enabled = Number.isFinite(encounterId) && encounterId > 0;

  const { data, isLoading } = useListAgendaItems(encounterId, {
    query: { enabled },
  });

  if (!enabled) {
    return (
      <HorizontalFlexbox
        alignItems="center"
        justifyContent="center"
        className="rounded-lg bg-muted/40 p-3 text-center"
      >
        <Text appearance="system">Invalid encounter id.</Text>
      </HorizontalFlexbox>
    );
  }

  if (isLoading) {
    return (
      <HorizontalFlexbox
        alignItems="center"
        justifyContent="center"
        className="rounded-lg bg-muted/40 p-3"
      >
        <Spinner />
      </HorizontalFlexbox>
    );
  }

  const completedItems = (data?.data.items ?? []).filter(isCompleted);

  if (completedItems.length === 0) {
    return (
      <HorizontalFlexbox
        alignItems="center"
        justifyContent="center"
        className="rounded-lg bg-muted/40 p-3 text-center"
      >
        <Text appearance="system">
          No completed agenda items from this visit.
        </Text>
      </HorizontalFlexbox>
    );
  }

  return (
    <VerticalFlexbox
      as="ul"
      gap="xs"
      data-testid="completed-agenda-items"
      aria-label="Completed agenda items"
      className="list-none"
    >
      {completedItems.map((item) => (
        <CompletedAgendaItemRow key={item.id} item={item} />
      ))}
    </VerticalFlexbox>
  );
};

interface CompletedAgendaItemRowProps {
  item: AgendaItem;
}

const CompletedAgendaItemRow = ({ item }: CompletedAgendaItemRowProps) => {
  const aiCompleted = item.completedBy === "ai_agent";

  return (
    <li
      data-testid="completed-agenda-item"
      aria-label={`${item.description}${
        aiCompleted ? " — completed by AI" : " — completed"
      }`}
      className="list-none"
    >
      <HorizontalFlexbox className="items-stretch overflow-hidden rounded-lg border border-border bg-card shadow-soft">
        <div aria-hidden className="w-1 shrink-0 bg-success" />
        <HorizontalFlexbox
          alignItems="center"
          justifyContent="space-between"
          gap="sm"
          className="flex-1 px-4 py-3"
        >
          <HorizontalFlexbox
            alignItems="center"
            gap="xs"
            className="min-w-0 flex-1"
          >
            <FileText className="size-4 shrink-0 text-accent" />
            <Text sizing="sm" className="line-clamp-2">
              {item.description}
            </Text>
          </HorizontalFlexbox>
          {aiCompleted ? (
            <span
              aria-label="Completed by AI"
              className="shrink-0 rounded-md bg-accent/15 px-1.5 py-0.5 text-[10px] font-semibold text-accent"
            >
              AI
            </span>
          ) : null}
        </HorizontalFlexbox>
      </HorizontalFlexbox>
    </li>
  );
};
